import React, { useState, useEffect } from 'react';
import { 
  XMarkIcon, 
  UserIcon, 
  UsersIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import useCourseUsers from '../hooks/useCourseUsers';

const AssignmentModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  course,
  mode // 'author' only (students handled by EnrollmentModal)
}) => {
  const [selectedAuthor, setSelectedAuthor] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);

  const { authors, users, loading: usersLoading, getUserById } = useCourseUsers();

  useEffect(() => {
    if (isOpen && course) {
      // Initialize with current assignments
      // Handle both new (author_id) and old (author.id) structure
      const authorId = course.author_id || course.author?.id;
      setSelectedAuthor(authorId || '');
      setSearchQuery(''); // Reset search when modal opens
    }
  }, [isOpen, course, mode]);

  // Filter authors based on search query
  const filteredAuthors = authors.filter(author => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    const name = (author.name || '').toLowerCase();
    const email = (author.email || '').toLowerCase();
    const role = Array.isArray(author.role) ? author.role.join(' ').toLowerCase() : (author.role || '').toLowerCase();
    
    return name.includes(query) || email.includes(query) || role.includes(query);
  });

  const handleSave = async () => {
    setLoading(true);
    try {
      let updateData = {};
      
      if (selectedAuthor) {
        // Get the full author data
        const authorData = getUserById(selectedAuthor);
        
        if (authorData) {
          // Replace the entire author object with full author data
          updateData = {
            author_id: selectedAuthor,
            author: {
              id: authorData.id,
              name: authorData.name || 'Unknown Author',
              image_url: authorData.image_url || null,
              status: authorData.status || 'Online',
              zoomId: authorData.zoomId || course.author?.zoomId || course.author_details?.zoomId || ''
            }
          };
        } else {
          // Fallback if author not found
          updateData = {
            author_id: selectedAuthor,
            author: {
              id: selectedAuthor,
              name: 'Unknown Author',
              image_url: null,
              status: 'Online',
              zoomId: course.author?.zoomId || course.author_details?.zoomId || ''
            }
          };
        }
      } else {
        // Remove author assignment
        updateData = {
          author_id: '',
          author: null
        };
      }

      await onSave(updateData, course.id);
      onClose();
    } catch (error) {
      console.error('Error saving assignment:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedAuthor('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
        {/* Header */}
        <div className="flex items-center justify-between pb-3 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <>
              <UserIcon className="h-5 w-5 mr-2 text-blue-600" />
              Assign Course Author
            </>
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Course Info */}
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center">
            <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
              <span className="text-blue-600 font-semibold text-lg">
                {course?.name?.charAt(0) || 'C'}
              </span>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900">{course?.name}</h4>
              <p className="text-xs text-gray-500">
                {course?.category || 'General'} • {course?.status || 'Draft'}
              </p>
            </div>
          </div>
        </div>

        {/* Assignment Content */}
        <div className="mt-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Select Course Author/Instructor
            </label>
            
            {/* Search Input */}
            <div className="relative mb-4">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search authors by name, email, or role..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={usersLoading}
              />
            </div>
            
            {usersLoading ? (
              <div className="text-center py-4">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <p className="text-sm text-gray-500 mt-2">Loading authors...</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {/* No Author Option */}
                <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="radio"
                    name="author"
                    value=""
                    checked={selectedAuthor === ''}
                    onChange={(e) => setSelectedAuthor(e.target.value)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <div className="ml-3">
                    <div className="text-sm font-medium text-gray-900">No Author</div>
                    <div className="text-xs text-gray-500">Unassign current author</div>
                  </div>
                </label>
                
                {/* Author Options */}
                {filteredAuthors.length > 0 ? (
                  filteredAuthors.map((author) => (
                  <label key={author.id} className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="radio"
                      name="author"
                      value={author.id}
                      checked={selectedAuthor === author.id}
                      onChange={(e) => setSelectedAuthor(e.target.value)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center ml-3 mr-3">
                      <UserIcon className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">{author.name}</div>
                      <div className="text-xs text-gray-500">
                        {author.email} • {author.role || 'Instructor'}
                      </div>
                    </div>
                  </label>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-500">No authors found</p>
                    {searchQuery && (
                      <p className="text-xs text-gray-400">Try adjusting your search terms</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end pt-6 border-t border-gray-200 gap-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading || usersLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <CheckIcon className="h-4 w-4 mr-2" />
                Assign Author
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignmentModal;
